import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import {
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
} from "../../../../constants/app";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import Roles from "../../../../models/roles";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import { showNotificationAction } from "../../../../store/application/slice";
import { getAddressSuggestionsThunk } from "../../../../store/auth/thunks";
import { AddressSuggestion } from "../../../../store/auth/types";
import { editCompletedPersonalDetail } from "../../../../store/onboarding/thunk";
import { TFileUploadsResponse } from "../../../../store/uploads/types";
import { createValidationSchema } from "../../../../utils/createValidationSchema";
import SaveNotification from "../../../SaveNotification";
import CustomButton from "../../../UI/Buttons/CustomButton";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import Checkbox from "../../../UI/Inputs/Checkbox";
import DocumentUpload from "../../../UI/Inputs/DocumentUpload";
import ImageUpload from "../../../UI/Inputs/ImageUpload";
import TextInput from "../../../UI/Inputs/TextInput";
import ErrorMessage from "../../../UI/Tooltips/ErrorMessage";

interface CompletedPersonalDetailProps {
  handleClose: (value: boolean) => void;
}

const CompletedPersonalDetail: React.FC<CompletedPersonalDetailProps> = ({
  handleClose,
}) => {
  const dispatch = useDispatch();

  const personalDetails = useSelector(
    (state) => state.onboarding.userpersonaldetails
  );
  const userpersonaldetails = useSelector(
    (state) =>
      state.onboarding.userpersonaldetails ??
      state.superadmin.reviewCandidateData
  );
  const error = useSelector((state) => state.auth.error);
  const inProgress = useSelector((state) => state.onboarding.inProgress);
  const user = useSelector((state) => state.auth.user);

  const isSuperAdmin = (user?.role?.name as Roles) === Roles.SUPER_ADMIN;

  const [uploadValidationError, setUploadValidationError] = useState<
    string | null
  >(null);
  const [showNotification, setShowNotification] = useState(false);
  const [searchInput, setSearchInput] = useState<string>("");
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");
  const [addressSuggestions, setAddressSuggestions] = useState<
    AddressSuggestion[]
  >([]);
  const [hasAddressSelected, setHasAddressSelected] = useState<boolean>(false);
  const [selectPermanentEmployment, setSelectPermanentEmployment] =
    useState<boolean>(personalDetails?.profile?.permanent_employment ?? false);

  // useEffect(() => {
  //   if (addressSuggestionArr.length > 0) {
  //     setAddressSuggestions(addressSuggestionArr);
  //   }
  // }, [addressSuggestionArr]);

  /**
   * Debounces the `selectedKeyword` state to avoid excessive API calls while the user is typing.
   * The debounced value is used to trigger the API call for fetching candidates.
   */
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  /**
   * Handles the change in the search input by updating the `selectedKeyword` state.
   * It also resets the current page to 1 if the search input is empty or exceeds the default length.
   *
   */
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setSelectedKeyword(debouncedSearch);
    }
  }, [debouncedSearch]);

  // useEffect(() => {
  //   if (selectedKeyword !== "") {
  //     const handleAddressSuggestions = async (selectedKeyword: string) => {
  //       await dispatch(getAddressSuggestionsThunk(selectedKeyword));
  //     };

  //     handleAddressSuggestions(selectedKeyword);
  //   } else {
  //     dispatch(resetAddressSuggestionsListAction());
  //   }
  // }, [dispatch, selectedKeyword]);

  const handleAddressSuggestions = async (selectedKeyword: string) => {
    const response = await dispatch(
      getAddressSuggestionsThunk(selectedKeyword)
    );

    if (isFulfilled(response)) {
      setAddressSuggestions(response.payload.data);
    } else if (searchInput.length === 0) {
      setAddressSuggestions([]);
    } else {
      setAddressSuggestions([]);
    }
  };

  const handleAddressChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = e.target.value;
    setHasAddressSelected(false);
    setFieldValue("address", inputValue);
    if (inputValue.length > 2 && !hasAddressSelected) {
      handleAddressSuggestions(inputValue);
    }
  };

  const handleSelectAddress = (selectedAddress: string) => {
    setFieldValue("address", selectedAddress);
    setAddressSuggestions([]); // Hide dropdown after selection
    setSelectedKeyword("");
    setHasAddressSelected(true);
  };

  const {
    handleSubmit,
    handleChange,
    values,
    errors,
    touched,
    setFieldValue,
    setFieldError,
  } = useFormik({
    validationSchema: createValidationSchema("edit_completed_personal_details"),
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      address: personalDetails?.address || "",
      resume: personalDetails?.profile?.resume || "",
      resume_name: personalDetails?.profile?.resume_name || "",
      phone_number: personalDetails?.phone_number || "",
      permanent_employment: personalDetails?.profile.permanent_employment
        ? personalDetails?.profile.permanent_employment
        : false,
    },
    onSubmit: async () => {
      const payload = { ...values };
      try {
        const result = await dispatch(editCompletedPersonalDetail(payload));
        if (isFulfilled(result)) {
          dispatch(showNotificationAction("Saved"));
          handleClose(false);
        }
      } catch (error) {
        throw new Error(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
      }
    },
  });

  /** Update the value for all document uploads dynamically */
  const handleFileSelect = (
    file: TFileUploadsResponse,
    fileName: string,
    deleteFile: boolean = false
  ) => {
    if (deleteFile) {
      setFieldValue("resume", "");
      setFieldValue("resume_name", "");
      return;
    } else {
      setFieldValue("resume", file.data.resume);
      setFieldValue("resume_name", fileName);
      setFieldError("resume", "");
    }
  };

  const handlePermenentEmploymentCheckBox = (checked: boolean) => {
    setSelectPermanentEmployment(checked ? true : false);
  };

  useEffect(() => {
    setFieldValue("permanent_employment", selectPermanentEmployment);
  }, [setFieldValue, selectPermanentEmployment]);

  return (
    <>
      <div className="d-flex flex-column gap-15 fw-400 text-black">
        <div className="fs-22">Edit Personal Details</div>
        <div className="d-flex flex-column flex-lg-row gap-30">
          <div className="d-flex align-self-start">
            <div className="position-relative rounded-circle p-7px">
              <ImageUpload
                Apierror={error && error?.errors?.image?.[0]}
                setUploadValidationError={setUploadValidationError}
                previewImage={
                  isSuperAdmin
                    ? userpersonaldetails?.photo ?? ""
                    : user?.photo ?? ""
                }
              />
            </div>
            <div className="w-100 order-3">
              {uploadValidationError && (
                <ErrorMessage message={uploadValidationError as string} />
              )}
            </div>
          </div>
          <div className="d-flex flex-column gap-10 flex-fill">
            <div className="position-relative">
              <TextInput
                value={values.phone_number || ""}
                field={"phone_number"}
                label={"Mobile Number"}
                onChange={handleChange}
                touched={touched.phone_number! as boolean}
                error={errors.phone_number as string}
                placeholder={" "}
              />
            </div>
            <div className="position-relative">
              <TextInput
                field={"address"}
                value={values.address}
                label={"Home Address"}
                onChange={handleAddressChange}
                touched={touched.address! as boolean}
                error={errors.address as string}
                placeholder={" " + ""}
              />
              {/* Address Suggestions Dropdown */}
              {addressSuggestions.length > 0 ? (
                <div className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100">
                  {addressSuggestions.map((addr, index) => (
                    <div
                      key={index}
                      className="px-15px py-8px d-flex align-items-center cursor-pointer hover-bg-off-white text-break"
                      onClick={() => handleSelectAddress(addr.description)}
                    >
                      {addr.description}
                    </div>
                  ))}
                </div>
              ) : selectedKeyword.length > 0 &&
                addressSuggestions.length === 0 ? (
                <div className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100 py-10px pl-10px">
                  No Address Found
                </div>
              ) : (
                ""
              )}
            </div>
            <div className="mt-3 mb-3">
              <div className="fs-16">Please upload your resume:</div>
              <DocumentUpload
                onFileSelect={handleFileSelect}
                type={"resume"}
                required={true}
                isPopup={true}
              />
              {errors && <ErrorMessage message={errors.resume as string} />}
            </div>
            {/* <div className="d-flex flex-column gap-10"> */}
            <div className="theme-check">
              <Checkbox
                className="theme-check-input"
                id="parmenent_employment"
                checked={selectPermanentEmployment}
                onChange={handlePermenentEmploymentCheckBox}
                labelFontSize={"16"}
                label={"I am interested in permanent opportunities as well as casual shifts"}
                themeCheck={true}
              />
            </div>
            {/* </div> */}
          </div>
        </div>
        <div className="w-100 h-1px"></div>
        <div className="d-flex justify-content-end">
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-teal text-black"
            onClick={handleSubmit}
            isLoading={inProgress}
            contentClass="d-flex"
          >
            <RoundedTick color={inProgress ? "#aaaaaa" : "black"} />
            Save
          </CustomButton>
        </div>
      </div>

      {showNotification && (
        <SaveNotification
          message="Saved"
          onClose={() => setShowNotification(false)}
        />
      )}
    </>
  );
};

export default CompletedPersonalDetail;
